import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { TextField, MenuItem, Autocomplete, createFilterOptions, Skeleton } from "@mui/material";
import type { Metier } from "models";
import { MetierBox } from "components/Business";
import { stringUtil } from "@sdeapps/react-core";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: ({ competence, libelle }: Metier) => {
    const normalizedString = stringUtil.normalize(`${competence} ${libelle}`);
    return `${normalizedString} ${libelle}`;
  },
});

interface MetierAutoCompleteProps {
  value?: Metier | null;
  onChange?: (metier: Metier | null) => void;
  label?: string;
  placeholder?: string;
  metiers: Array<Metier>;
  size?: "small" | "medium";
  isLoading?: boolean;
  groupesParNiveau?: boolean;
}

export function MetierAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  metiers,
  size = "medium",
  isLoading = false,
  groupesParNiveau = false,
}: Readonly<MetierAutoCompleteProps>): ReactNode {
  const [input, setInput] = useState<string>("");
  const sortedMetiers = useMemo(
    () => metiers.toSorted((a, b) => a.niveau.localeCompare(b.niveau)),
    [metiers]
  );

  if (isLoading) {
    return <Skeleton height={56} />;
  }

  return (
    <Autocomplete
      value={sortedMetiers.find((o) => o.code === value?.code) ?? null}
      onChange={(_e, _value: Metier | null) => {
        onChange?.(_value);
      }}
      onInputChange={(_e, inputValue) => {
        setInput(inputValue);
      }}
      options={sortedMetiers}
      filterOptions={filterOptions}
      groupBy={groupesParNiveau ? (option) => `Métiers niveau ${option.niveau}` : undefined}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      isOptionEqualToValue={(o, v) => o.code === v.code}
      getOptionLabel={(option) => `${option.competence} ${option.libelle}`}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      renderValue={(metier) => input === "" && <MetierBox metier={metier} noWrap />}
      getOptionKey={({ code }) => code}
      renderOption={(props, metier) => (
        <MenuItem {...props} key={props.key}>
          <MetierBox metier={metier} noWrap />
        </MenuItem>
      )}
      noOptionsText="Aucun Métier ne correspond à votre recherche."
    />
  );
}
